/**
 * Centralized Query Key Factory
 *
 * All react-query cache keys in one place.
 * Follows the key factory pattern for consistent invalidation.
 */

export const queryKeys = {
  // --- Projects ---
  projects: {
    all: () => ['projects'] as const,
    detail: (id: string) => ['projects', id] as const,
    tasks: (projectId: string) => ['projects', projectId, 'tasks'] as const,
    timeEntries: (projectId: string) => ['projects', projectId, 'timeEntries'] as const,
    milestones: (projectId: string) => ['projects', projectId, 'milestones'] as const,
  },

  // --- Accounting ---
  accounting: {
    all: () => ['accounting'] as const,
    invoices: () => ['accounting', 'invoices'] as const,
    invoice: (id: string) => ['accounting', 'invoices', id] as const,
    bankAccounts: () => ['accounting', 'bankAccounts'] as const,
    bankTransactions: (accountId: string) => ['accounting', 'bankTransactions', accountId] as const,
  },

  // --- Calendar ---
  calendar: {
    all: ['calendar'] as const,
    events: (filters?: { eventType?: string; projectId?: string; fromDate?: string; toDate?: string }) =>
      filters ? ['calendar', 'events', filters] as const : ['calendar', 'events'] as const,
    event: (id: string) => ['calendar', 'events', 'detail', id] as const,
    reminders: () => ['calendar', 'reminders'] as const,
    reminder: (id: string) => ['calendar', 'reminders', id] as const,
  },

  // --- Hub ---
  hub: {
    all: () => ['hub'] as const,
    conversations: () => ['hub', 'conversations'] as const,
    conversation: (id: string) => ['hub', 'conversations', id] as const,
    contacts: () => ['hub', 'contacts'] as const,
    contact: (id: string) => ['hub', 'contacts', id] as const,
    companies: () => ['hub', 'companies'] as const,
    messages: (conversationId: string) => ['hub', 'messages', conversationId] as const,
  },

  // --- Links ---
  links: {
    all: () => ['links'] as const,
    byEntity: (entityId: string) => ['links', 'entity', entityId] as const,
  },

  // --- Google ---
  google: {
    all: () => ['google'] as const,
    connection: () => ['google', 'connection'] as const,
    emails: () => ['google', 'emails'] as const,
  },

  // --- Integrations ---
  integrations: {
    all: () => ['integrations'] as const,
    config: () => ['integrations', 'config'] as const,
  },

  // --- Templates ---
  templates: {
    all: () => ['templates'] as const,
    detail: (id: string) => ['templates', id] as const,
  },

  // --- Notifications ---
  notifications: {
    all: () => ['notifications'] as const,
    settings: () => ['notifications', 'settings'] as const,
  },

  // --- Timer ---
  timer: {
    all: () => ['timer'] as const,
    active: () => ['timer', 'active'] as const,
    entries: () => ['timer', 'entries'] as const,
  },

  // --- E-Invoices ---
  eInvoices: {
    all: () => ['eInvoices'] as const,
    list: () => ['eInvoices', 'list'] as const,
    detail: (id: string) => ['eInvoices', id] as const,
    settings: () => ['eInvoices', 'settings'] as const,
  },

  // --- Saved Links ---
  savedLinks: {
    all: () => ['savedLinks'] as const,
    list: () => ['savedLinks', 'list'] as const,
    detail: (id: string) => ['savedLinks', id] as const,
  },

  // --- Team ---
  team: {
    all: () => ['team'] as const,
    members: () => ['team', 'members'] as const,
    invites: () => ['team', 'invites'] as const,
  },

  // --- AI Settings ---
  aiSettings: {
    all: () => ['aiSettings'] as const,
    settings: () => ['aiSettings', 'settings'] as const,
  },

  // --- Banking (Open Banking / TrueLayer) ---
  banking: {
    all: () => ['banking'] as const,
    connections: (accountId?: string) => ['banking', 'connections', accountId] as const,
    connection: (id: string) => ['banking', 'connections', 'detail', id] as const,
    accounts: (accountId?: string, connectionId?: string) => ['banking', 'accounts', accountId, connectionId] as const,
    account: (id: string) => ['banking', 'accounts', 'detail', id] as const,
    transactions: (bankAccountId: string) => ['banking', 'transactions', bankAccountId] as const,
    transaction: (id: string) => ['banking', 'transactions', 'detail', id] as const,
    unreconciledTransactions: (accountId: string) => ['banking', 'unreconciled', accountId] as const,
    balanceHistory: (bankAccountId: string) => ['banking', 'balanceHistory', bankAccountId] as const,
    totalBalance: (accountId: string, currency?: string) => ['banking', 'totalBalance', accountId, currency] as const,
    syncStatus: (id: string) => ['banking', 'syncStatus', id] as const,
    hasBankConnections: (accountId: string) => ['banking', 'hasConnections', accountId] as const,
  },
};
